import { useEffect, useMemo, useReducer, useRef, useState } from "react";

import { Alert, Box, Paper, Stack } from "@mui/material";
import { DataGrid, useGridApiRef } from "@mui/x-data-grid";
import type { GridColDef } from "@mui/x-data-grid";

import { FilterTextField } from "~/components/shared";
import { LegacyRegistryService } from "~/services";
import { useTableExport } from "~/utils/tableExport";

const columns: GridColDef[] = [
  { field: "name", headerName: "Name", flex: 2 },
  { field: "address", headerName: "Address", flex: 2 },
  { field: "city", headerName: "City", flex: 2 },
  { field: "state", headerName: "State", flex: 1 },
  { field: "postalCode", headerName: "Postal Code", flex: 1 },
  { field: "county", headerName: "County", flex: 1 },
  { field: "phone", headerName: "Phone", flex: 1 },
  { field: "email", headerName: "Email", flex: 2 },
  { field: "handsOnCare", headerName: "Hands On Care", flex: 1 },
  { field: "transportation", headerName: "Transportation", flex: 1 },
  { field: "willTravel", headerName: "Will Travel", flex: 1 },
  { field: "miles", headerName: "Miles", flex: 1 },
  { field: "minHours", headerName: "Min Hours", flex: 1 },
];

type TableFilters = {
  search: string;
};

const Registry = () => {
  const tableRef = useGridApiRef();
  const exportTable = useTableExport();

  const [isLoading, setIsLoading] = useState(false);

  const registryAbortController = useRef<AbortController>(new AbortController());
  const registryExportAbortController = useRef<AbortController>(new AbortController());

  const [tableFilters, setTableFilters] = useReducer(
    (state: TableFilters, action: { key: keyof TableFilters; value: any }) => ({
      ...state,
      [action.key]: action.value,
    }),
    {
      search: "",
    },
  );

  const dataSource = useMemo(
    () => ({
      getRows: async (params: any) => {
        registryAbortController.current?.abort();
        registryAbortController.current = new AbortController();

        setIsLoading(true);

        try {
          const registryResponse = await LegacyRegistryService.getProviderRegistry(
            tableFilters.search,
            params.paginationModel.page,
            params.paginationModel.pageSize,
            params.sortModel.map(({ field, sort }: { field: string; sort: string }) => `${field}:${sort}`),
            {
              signal: registryAbortController.current?.signal,
            },
          );

          if (LegacyRegistryService.getPendingRequestCount() === 0) {
            setIsLoading(false);
          }

          return {
            rows: registryResponse?.data?.items ?? [],
            rowCount: registryResponse?.data?.total ?? 0,
          };
        } catch (error: any) {
          if (error?.name !== "CanceledError" && error?.name !== "AbortError") {
            setIsLoading(false);
          }
          throw error;
        }
      },
    }),
    [tableFilters],
  );

  useEffect(
    () => () => {
      registryAbortController.current?.abort();
      registryAbortController.current = new AbortController();
      registryExportAbortController.current?.abort();
      registryExportAbortController.current = new AbortController();
    },
    [],
  );

  return (
    <Box sx={{ p: 1, height: "100%", justifyContent: "center", alignItems: "center", overflow: "auto" }}>
      <Paper elevation={1} sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
        <Alert severity="warning">
          The Caregiver Registry is compiled from survey data submitted by caregivers who have indicated interest in serving additional
          participants/members. All submitted responses are retained in the registry; therefore, some information may not reflect current
          availability and may include outdated, incomplete, or inaccurate details.
        </Alert>
        <Stack alignItems="start" direction="row" flexWrap="wrap" gap={1} overflow="auto" p={1}>
          <FilterTextField
            filterKey="search"
            filters={tableFilters}
            label="Search"
            setFilters={setTableFilters}
            sx={{
              flexGrow: 1,
              width: 0,
              minWidth: "10rem",
            }}
          />
        </Stack>
        <DataGrid
          apiRef={tableRef}
          columns={columns}
          dataSource={dataSource}
          getRowId={({ registryEntryId }) => registryEntryId ?? ""}
          loading={isLoading}
          slotProps={{
            toolbar: {
              onExport: async (format) => {
                registryExportAbortController.current?.abort();
                registryExportAbortController.current = new AbortController();

                const data = await LegacyRegistryService.exportProviderRegistryCsv(
                  tableFilters.search,
                  (tableRef.current?.getSortModel() ?? []).map(({ field, sort }) => `${field}:${sort ?? "asc"}`),
                  {
                    signal: registryExportAbortController.current?.signal,
                  },
                );

                exportTable(data, "Registry", format);
              },
            },
          }}
          sx={{
            "& .MuiDataGrid-columnHeaderTitle": {
              color: (theme) => theme.palette.custom.main,
            },
            ...(isLoading && {
              ".MuiDataGrid-columnHeaders": {
                pointerEvents: "none",
              },
            }),
          }}
          disableRowSelectionOnClick
        />
      </Paper>
    </Box>
  );
};

export default Registry;
